import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { Sa360Icon } from '../../assets/icons/BrandIcons';
import api from '../../config/api';

const GOOGLE_BLUE = '#4285F4';

// Shown after the Search Ads 360 Google sign-in: the login can usually reach several client accounts
// (directly or under manager accounts). Each account the user ticks becomes its own connector.
const Sa360Selection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [tempId, setTempId] = useState(null);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const temp_id = searchParams.get('temp_id');
    if (!temp_id) {
      setError('Invalid session. Please try authenticating again.');
      setLoading(false);
      return;
    }
    setTempId(temp_id);
    loadAccounts(temp_id);
  }, [searchParams]);

  const loadAccounts = async (temp_id) => {
    try {
      setLoading(true);
      const response = await api.get('/api/connectors/sa360/accounts', { params: { temp_id } });
      if (response.data.success) {
        setAccounts(response.data.accounts || []);
        setUserEmail(response.data.user_email || '');
      } else {
        setError(response.data.message || 'Failed to load Search Ads 360 accounts');
      }
    } catch (err) {
      console.error('Error loading Search Ads 360 accounts:', err);
      setError(err.response?.data?.message || 'Failed to load Search Ads 360 accounts');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    if (connecting) return;
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleConnect = async () => {
    if (selected.length === 0) {
      setError('Please choose at least one account to connect');
      return;
    }
    setConnecting(true);
    setError('');
    try {
      const response = await api.post('/api/connectors/sa360/select', {
        temp_id: tempId,
        customer_ids: selected
      });
      if (response.data.success) {
        try {
          await api.post('/api/connectors/sa360/cleanup', { temp_id: tempId });
        } catch (cleanupError) {
          console.warn('Failed to cleanup temp data:', cleanupError);
        }
        navigate('/connectors', {
          state: { message: response.data.message || 'Search Ads 360 connected', severity: 'success' }
        });
      } else {
        // Partial success: keep the page so the failed accounts can be retried.
        const failedIds = (response.data.failures || []).map(f => f.customer_id);
        setSelected(failedIds);
        setError(response.data.message || 'Failed to connect the selected accounts');
      }
    } catch (err) {
      console.error('Error connecting Search Ads 360 accounts:', err);
      setError(err.response?.data?.message || 'Failed to connect the selected accounts');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading Search Ads 360 accounts...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/connectors')} sx={{ mr: 2 }}>
          Back
        </Button>
        <Box sx={{ mr: 2, display: 'flex', flexShrink: 0 }}>
          <Sa360Icon size={32} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Choose Search Ads 360 Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userEmail ? `Signed in as ${userEmail}. ` : ''}Each account you choose becomes its own connector.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select accounts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The connector syncs the account's campaigns, ad groups, ads, keywords, assets and daily performance, in the account's own currency and time zone.
          </Typography>

          {accounts.length === 0 ? (!error && (
            <Alert severity="warning">
              No Search Ads 360 client accounts found for this Google login. Ask your Search Ads 360 admin to give this login access, then connect again.
            </Alert>
          )) : (
            <Grid container spacing={2}>
              {accounts.map((account) => {
                const isSelected = selected.includes(account.id);
                return (
                  <Grid item xs={12} md={6} key={account.id}>
                    <Card
                      variant="outlined"
                      onClick={() => toggle(account.id)}
                      sx={{
                        height: '100%',
                        cursor: connecting ? 'default' : 'pointer',
                        borderColor: isSelected ? GOOGLE_BLUE : undefined,
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
                      }}
                    >
                      <CardContent>
                        <FormControlLabel
                          onClick={(e) => e.stopPropagation()}
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggle(account.id)}
                              disabled={connecting}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', mr: 2 }}>
                                  <Sa360Icon size={22} />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {account.name || account.id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {account.id}{account.manager_name ? ` · ${account.manager_name}` : ''}
                                  </Typography>
                                  {account.alreadyConnected && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Chip
                                        label={account.connectedBrandName
                                          ? `Connected · ${account.connectedBrandName}`
                                          : 'Already connected'}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                              <Divider sx={{ my: 1 }} />
                              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                {account.currency && <Chip size="small" label={account.currency} />}
                                {account.time_zone && <Chip size="small" variant="outlined" label={account.time_zone} />}
                                {account.status && account.status !== 'ENABLED' && (
                                  <Chip size="small" color="warning" label={account.status} />
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      {accounts.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/connectors')} disabled={connecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={selected.length === 0 || connecting}
            startIcon={connecting ? <CircularProgress size={20} /> : <CheckIcon />}
            sx={{ backgroundColor: GOOGLE_BLUE, '&:hover': { backgroundColor: '#3367D6' } }}
          >
            {connecting ? 'Connecting...' : `Connect ${selected.length || ''} Account${selected.length === 1 ? '' : 's'}`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Sa360Selection;
